import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { API_BASE_URL } from "../apiConfig";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "", general: "" });

  const [loading, setLoading] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const { setCurrentUser, setToken } = useAuth();

  const isValidEmail = (value) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value);

  // Disable scrolling
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({ email: "", password: "", general: "" });
    setShowResendOption(false);

    const nextErrors = { email: "", password: "", general: "" };
    if (!email.trim()) nextErrors.email = "Email is required.";
    else if (!isValidEmail(email)) nextErrors.email = "Enter a valid email address.";
    if (!password) nextErrors.password = "Password is required.";
    if (nextErrors.email || nextErrors.password) {
      setErrors(nextErrors);
      return;
    }

    // Send login request to backend
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's an unverified email (403 status)
        if (response.status === 403 && data.message && !data.message.toLowerCase().includes("locked")) {
          setShowResendOption(true);
        }
        setErrors({ email: "", password: "", general: data.message || "Login failed" });
        return;
      }

      // Login successful - detect user side vs admin side from server response
      const user = data.user || {};
      const role = (user.role || "").toLowerCase();

      // Prevent admins and mobile staff from logging in through the customer portal
      if (role === "admin" || role === "staff" || role === "production" || role === "delivery") {
        setErrors({ email: "", password: "", general: "Admins and Staff must use their designated portal or mobile app." });
        setLoading(false);
        return;
      }

      localStorage.setItem("user", JSON.stringify({ ...user, role: role || user.role }));
      localStorage.setItem("token", data.token);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      setCurrentUser({ ...user, role: role || user.role });
      if (setToken && data.token) setToken(data.token);

      // User side: open HomePage
      
      navigate("/home");
    } catch (error) {
      console.error("Login error:", error);
      setErrors({ email: "", password: "", general: "Server error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowResendOption(false);
        // Redirect to verify page so they can enter the code
        navigate(`/verify?email=${encodeURIComponent(email)}`);
      } else {
        setErrors({ ...errors, general: data.message || "Failed to resend verification email." });
      }
    } catch (error) {
      console.error("Resend error:", error);
      
    } finally {
      setResendLoading(false);
    }
  };

  const [logoClickCount, setLogoClickCount] = useState(0);

  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    if (newCount === 3) {
      navigate("/admin-login");
    } else {
      setLogoClickCount(newCount);
      // Optional: reset count after 2 seconds
      setTimeout(() => setLogoClickCount(0), 2000);
    }
  };

  const logoImg = require('../assets/logonobg.png');

  const css = `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Montserrat:wght@300;400;500;600&display=swap');
 
        * { box-sizing: border-box; margin: 0; padding: 0; }
 
        .login-page {
            min-height: 100vh;
            width: 100%;
            display: flex;
            font-family: 'Montserrat', sans-serif;
            overflow: hidden;
            background-color: #FFFFFF;
        }
 
        .login-left {
            width: 45%;
            background-color: #EDF0EE; /* Soft earth/sage grey */
            background-image: linear-gradient(135deg, #F5F7F5 0%, #E6EAE7 100%);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 24px;
            padding: 60px 40px;
            position: relative;
            border-right: 1px solid rgba(193, 208, 205, 0.5); /* Subtle border matching the sage theme */
        }
        .logo-wrap {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
        }
        .logo-img {
            width: 130px;
            height: 130px;
            object-fit: contain;
            filter: drop-shadow(0 4px 24px rgba(46, 75, 76, 0.15));
        }
        .brand-name {
            font-family: 'Cormorant Garamond', serif;
            font-size: 38px;
            font-weight: 600;
            color: #1F3636; /* Deeper forest green */
            letter-spacing: 3px;
            text-align: center;
            line-height: 1.15;
            text-transform: uppercase;
        }
        .brand-sub {
            font-size: 11px;
            font-weight: 600;
            color: #A3855E; /* Warm brassy gold accent */
            letter-spacing: 5px;
            text-transform: uppercase;
            text-align: center;
            margin-top: -10px;
        }

        .brand-quote {
            position: absolute;
            bottom: 60px;
            font-family: 'Cormorant Garamond', serif;
            font-style: italic;
            font-size: 15px;
            color: #6E8886;
            text-align: center;
            max-width: 260px;
            line-height: 1.6;
            letter-spacing: 0.5px;
        }
        .brand-quote::before {
            content: "";
            display: block;
            width: 30px;
            height: 1px;
            background-color: #C1D0CD;
            margin: 0 auto 16px auto;
        }
 
        /* ── RIGHT PANEL ── */
        .login-right {
            flex: 1;
            background-color: #FFFFFF;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 80px;
        }
        .login-box {
            width: 100%;
            max-width: 380px;
        }
        .login-welcome {
            font-family: 'Cormorant Garamond', serif;
            font-size: 42px;
            font-weight: 400;
            color: #2E4B4C;
            margin-bottom: 6px;
            letter-spacing: 1px;
            text-align: center;
        }
        .login-tagline {
            font-size: 11px;
            font-weight: 500;
            color: rgba(46, 75, 76, 0.6);
            letter-spacing: 3px;
            text-transform: uppercase;
            margin-bottom: 44px;
            text-align: center;
        }
 
        /* inputs */
        .login-input {
            width: 100%;
            padding: 14px 16px;
            background: #FAFBFA; /* Very pale mint/white */
            border: 1px solid #C1D0CD; /* Sage green outline */
            border-radius: 6px;
            color: #1F3636;
            font-family: 'Montserrat', sans-serif;
            font-size: 14px;
            font-weight: 500;
            outline: none;
            margin-bottom: 16px;
            transition: border-color 0.3s, box-shadow 0.3s, background-color 0.3s;
        }
        .login-input::placeholder { color: #8F9E9C; }
        .login-input:hover { border-color: #92ACA7; }
        .login-input:focus {
            background: #FFFFFF;
            border-color: #2E4B4C;
            box-shadow: 0 0 0 3px rgba(46, 75, 76, 0.12);
        }
 
        .password-container {
            position: relative;
            width: 100%;
            margin-bottom: 16px;
        }
        .password-container .login-input {
            margin-bottom: 0;
            padding-right: 40px;
        }
        .password-toggle {
            position: absolute;
            right: 14px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            color: #6E8886;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            border-radius: 40px;
            transition: color 0.2s, background-color 0.2s;
        }
        .password-toggle:hover {
            color: #2E4B4C;
            background-color: rgba(46, 75, 76, 0.05);
        }

 
        /* button */
        .login-btn {
            width: 100%;
            padding: 14px;
            background: #2E4B4C;
            color: #F8FAF9;
            border: none;
            border-radius: 6px;
            font-family: 'Montserrat', sans-serif;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 2px;
            text-transform: uppercase;
            cursor: pointer;
            margin-top: 6px;
            transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
        }
        .login-btn:hover:not(:disabled) {
            background: #213C3D;
            transform: translateY(-1px);
            box-shadow: 0 4px 14px rgba(46, 75, 76, 0.25);
        }
        .login-btn:disabled { opacity: 0.5; background: #668383; cursor: not-allowed; }

        .resend-btn {
            width: 100%;
            padding: 14px;
            background: #FFFFFF;
            color: #2E4B4C;
            border: 1px solid #2E4B4C;
            border-radius: 6px;
            font-family: 'Montserrat', sans-serif;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 1px;
            text-transform: uppercase;
            cursor: pointer;
            margin-bottom: 16px;
            transition: background 0.2s, color 0.2s, transform 0.15s;
        }
        .resend-btn:hover:not(:disabled) {
            background: #2E4B4C;
            color: #FFFFFF;
            transform: translateY(-1px);
        }
 
        /* errors */
        .err-text {
            color: #e74c3c;
            font-size: 11px;
            margin-top: -8px;
            margin-bottom: 10px;
            letter-spacing: 0.3px;
        }
        .err-general {
            color: #e74c3c;
            font-size: 12px;
            text-align: center;
            margin: 8px 0;
            margin-bottom: 16px;
        }
 
        /* links */
        .forgot-link {
            display: block;
            text-align: right;
            margin-top: -8px;
            margin-bottom: 24px;
            color: #4B6E6D;
            font-size: 12px;
            text-decoration: none;
            letter-spacing: 0.4px;
            transition: color 0.2s;
            font-weight: 500;
        }
        .forgot-link:hover { color: #A3855E; }

        .signup-text {
            text-align: center;
            margin-top: 24px;
            font-size: 13px;
            color: #6E8886;
            letter-spacing: 0.4px;
        }
        .signup-link {
            color: #2E4B4C;
            font-weight: 700;
            text-decoration: none;
            transition: color 0.2s;
        }
        .signup-link:hover { color: #A3855E; }

        .back-link:hover { color: #2E4B4C; }

        @media (max-width: 768px) {
            .login-left { display: none; }
            .login-right { padding: 40px 20px; justify-content: flex-start; padding-top: 60px; }
            .login-welcome { font-size: 32px; }
            .login-tagline { margin-bottom: 30px; }
            .mobile-logo-wrap { display: flex; flex-direction: column; align-items: center; margin-bottom: 24px; }
            .mobile-logo-img { width: 80px; height: 80px; object-fit: contain; filter: drop-shadow(0 2px 12px rgba(46, 75, 76, 0.15)); }
        }
        @media (min-width: 769px) {
            .mobile-logo-wrap { display: none; }
        }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="login-page">
        {/* ── LEFT: Logo + Brand ── */}
        <div className="login-left">
            <div className="logo-wrap">
                <img 
                    src={logoImg} 
                    alt="Ukit Logo" 
                    className="logo-img" 
                    onClick={handleLogoClick}
                    style={{ cursor: 'default' }}
                />
                <div>
                    <div className="brand-name">Úkit<br />Kapampangan</div>
                    <div className="brand-sub">Est. Furniture Crafts</div>
                </div>
            </div>

            <div className="brand-quote">
                "Crafting timeless wooden heirlooms for generations."
            </div>
        </div>

        {/* ── RIGHT: Login Form ── */}
        <div className="login-right">
            <div className="login-box">
                <div className="mobile-logo-wrap">
                    <img 
                        src={logoImg} 
                        alt="Ukit Logo" 
                        className="mobile-logo-img" 
                        onClick={handleLogoClick}
                        style={{ cursor: 'default' }}
                    />
                </div>
                <div className="login-welcome">Welcome</div>
                <div className="login-tagline">Customer Sign In</div>

                <form onSubmit={handleLogin} noValidate>
                    <input
                        type="email"
                        name="email"
                        autoComplete="off"
                        placeholder="Email Address"
                        className="login-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    {errors.email && <div className="err-text">{errors.email}</div>}

                    <div className="password-container">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            autoComplete="new-password"
                            placeholder="Password"
                            className="login-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    {errors.password && <div className="err-text">{errors.password}</div>}

                    <a
                        href="/forgot-password"
                        className="forgot-link"
                        onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}
                    >
                        Forgot password?
                    </a>

                    {errors.general && (
                        <div className="err-general">{errors.general}</div>
                    )}

                    {showResendOption && (
                        <button
                            type="button"
                            className="resend-btn"
                            onClick={handleResendVerification}
                            disabled={resendLoading}
                        >
                            {resendLoading ? "Sending..." : "Resend Verification Email"}
                        </button>
                    )}

                    <button
                        type="submit"
                        className="login-btn"
                        disabled={loading}
                    >
                        {loading ? "Logging In..." : "Sign In"}
                    </button>
                </form>

                <p className="signup-text">
                    Don't have an account?{" "}
                    <a
                        href="/signup"
                        className="signup-link"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate("/signup");
                        }}
                    >
                        Sign Up.
                    </a>
                </p>


            </div>
        </div>
      </div>
    </>
  );
}

export default LoginForm;
